/**
 * receivablesDataSource.ts — WatermelonDB → ReceivablesAgingInput bridge.
 *
 * Receivables are a *position as of now*, not activity in a range: the report
 * loads every unpaid SALE invoice for the store, regardless of when it was
 * raised, and ages each against `asOf`. Voided invoices are excluded so a
 * cancelled credit sale doesn't show as money owed.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { fetchInChunks } from '@/db/fetchInChunks';
import { Invoice, VOIDED_INVOICE_STATUSES } from '@/db/models/Billing';
import { Customer } from '@/db/models/Customers';
import type { ReceivablesAgingInput } from './receivablesAging';

/** Load a store's outstanding receivables as of `asOf` (defaults to now). */
export async function loadReceivables(
  storeId: string,
  asOf: Date = new Date(),
): Promise<ReceivablesAgingInput> {
  const invoices = await database.get<Invoice>('invoices').query(
    Q.where('store_id', storeId),
    Q.where('is_deleted', false),
    Q.where('status', Q.notIn(VOIDED_INVOICE_STATUSES)),
    Q.where('type', 'SALE'),
    Q.where('due_amount', Q.gt(0)),
  ).fetch();

  const customerIds = [
    ...new Set(invoices.map((i) => i.customerId).filter((id): id is string => !!id)),
  ];
  const customers = await fetchInChunks(customerIds, (batch) =>
    database.get<Customer>('customers').query(
      Q.where('id', Q.oneOf(batch)),
      Q.where('is_deleted', false),
    ).fetch(),
  );

  return {
    asOf,
    invoices: invoices.map((i) => ({
      customerId: i.customerId,
      dueAmount: i.dueAmount,
      invoiceDate: i.createdAt,
      dueDate: i.dueDate,
    })),
    customers: customers.map((c) => ({
      id: c.id,
      name: c.name,
      phone: c.phone,
      creditLimit: c.creditLimit,
    })),
  };
}
